import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Check, X, RefreshCw } from "lucide-react";

const REVIEW_STAGES = ["technical_review","legal_review","financial_review","committee_review","approved"];

export const ApplicationDetail = () => {
  const { aid } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const canAct = ["land_registrar","mining_commissioner","surveyor_general","compliance_officer","legal_officer","sovereign_admin"].includes(user?.role);

  const { data: a, isLoading } = useQuery({
    queryKey: ["application", aid],
    queryFn: async () => (await api.get(`/applications/${aid}`)).data,
  });

  const action = useMutation({
    mutationFn: async ({ stage, decision, reason }) => (await api.post(`/applications/${aid}/action`, { stage, decision, reason })).data,
    onSuccess: () => { toast.success("Workflow updated"); qc.invalidateQueries({ queryKey: ["application", aid] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  if (isLoading || !a) return <div className="text-[#B3C2CC]">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="page-app-detail">
      <Link to="/app/applications" className="text-[12px] text-[#7CBFDC] inline-flex items-center gap-1"><ArrowLeft size={12}/> Back to applications</Link>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 cad-panel p-6">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">{a.application_type.replace(/_/g, " ")}</div>
          <div className="font-display text-2xl font-bold text-[#F6F8FA] mt-1">{a.reference}</div>
          <div className="grid grid-cols-2 gap-3 mt-4 text-[13px]">
            <div><span className="text-[#7C93A5] text-[11px] uppercase font-mono-cad">Applicant</span><div className="text-[#F6F8FA]">{a.applicant_name}</div></div>
            <div><span className="text-[#7C93A5] text-[11px] uppercase font-mono-cad">Subject</span><div className="text-[#F6F8FA] font-mono-cad">{a.subject_ref || "—"}</div></div>
            <div><span className="text-[#7C93A5] text-[11px] uppercase font-mono-cad">Stage</span><div className="text-[#F6F8FA] capitalize">{a.stage?.replace(/_/g, " ")}</div></div>
            <div><span className="text-[#7C93A5] text-[11px] uppercase font-mono-cad">Status</span><div className="text-[#F6F8FA] capitalize">{a.status?.replace(/_/g, " ")}</div></div>
            <div><span className="text-[#7C93A5] text-[11px] uppercase font-mono-cad">Submitted</span><div className="text-[#F6F8FA] font-mono-cad">{(a.submitted_at || "").slice(0, 19).replace("T"," ")}</div></div>
            <div><span className="text-[#7C93A5] text-[11px] uppercase font-mono-cad">Priority</span><div className="text-[#F6F8FA] font-mono-cad">{(a.priority_timestamp || "").slice(0, 19).replace("T"," ")}</div></div>
          </div>
          <div className="mt-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5] mb-2">Workflow Ladder</div>
            <div className="flex items-center gap-1 flex-wrap">
              {["submitted", ...REVIEW_STAGES].map((s, i) => (
                <div key={s} className="flex items-center gap-1">
                  <span className={`cad-chip ${a.stage === s ? "cad-chip-gold" : "cad-chip"}`}>{s.replace(/_/g, " ")}</span>
                  {i < REVIEW_STAGES.length && <span className="text-[#415363]">→</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5] mb-2">Case history</div>
            <div className="space-y-2">
              {(a.history || []).map((h, i) => (
                <div key={i} className="cad-panel p-3">
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="capitalize text-[#F6F8FA]">{h.stage?.replace(/_/g, " ")} · {h.decision}</span>
                    <span className="font-mono-cad text-[#7C93A5]">{(h.timestamp || "").slice(0, 19).replace("T"," ")}</span>
                  </div>
                  {h.reason && <div className="text-[12px] text-[#B3C2CC] mt-1">{h.reason}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="cad-panel p-6 space-y-3 h-fit">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">Officer actions</div>
          <div className="font-display text-lg font-bold text-[#F6F8FA]">Advance workflow</div>
          {!canAct && <div className="text-[12px] text-[#B3C2CC]">Your role cannot advance this workflow. Sign in as an officer to act.</div>}
          {canAct && (
            <div className="space-y-2 pt-2">
              <button data-testid="action-approve" onClick={() => action.mutate({ stage: a.stage, decision: "approved" })}
                      className="cad-btn-primary w-full py-2 rounded-sm text-[13px] inline-flex items-center justify-center gap-1.5">
                <Check size={13}/> Advance / Approve
              </button>
              <button data-testid="action-return" onClick={() => action.mutate({ stage: a.stage, decision: "returned", reason: "Return for correction" })}
                      className="cad-btn-outline w-full py-2 rounded-sm text-[13px] inline-flex items-center justify-center gap-1.5">
                <RefreshCw size={13}/> Return for correction
              </button>
              <button data-testid="action-reject" onClick={() => action.mutate({ stage: a.stage, decision: "rejected", reason: "Statutory grounds" })}
                      className="cad-btn-outline w-full py-2 rounded-sm text-[13px] text-[#E67E7E] border-[#A93F3F]/50 inline-flex items-center justify-center gap-1.5">
                <X size={13}/> Reject
              </button>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};
